import {
  ChangeEvent,
  Dispatch,
  memo,
  SetStateAction,
  useMemo,
  ReactNode,
} from 'react';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
} from '@mui/material';
import useAlias from '@src/hooks/useAlias';
import { isValueInRange } from '@utils/mathHelpers';

const ValueOrLoader: React.FC<{ value: number }> = ({ value }) =>
  Number.isNaN(value) ? <CircularProgress size={12} /> : <>{value}</>;

interface Props {
  label: string | ReactNode;
  unit: string;
  minimum: number;
  maximum: number;
  value: string;
  currentValue: string | undefined;
  setValue: Dispatch<SetStateAction<string>>;
}

const CommandsNumberInput: React.FC<Props> = ({
  label,
  unit,
  minimum,
  maximum,
  value,
  currentValue,
  setValue,
}) => {
  const alias = useAlias();
  const translatedLabel = typeof label === 'string' ? alias(label) : label;

  const isValueValid = useMemo(() => {
    if (value.length === 0) {
      return true;
    }
    return isValueInRange(value, minimum, maximum);
  }, [value, minimum, maximum]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return (
    <FormControl
      variant='outlined'
      sx={{ width: '100%', mt: 2.5 }}
      error={!isValueValid}
    >
      <TextField
        size='small'
        label={translatedLabel}
        InputProps={{
          endAdornment: <InputAdornment position='end'>{unit}</InputAdornment>,
        }}
        variant='filled'
        type='number'
        value={value}
        onChange={handleChange}
        onFocus={(event) => event.target.select()}
        error={!isValueValid}
      />
      <FormHelperText>
        {alias('Range from')} <ValueOrLoader value={minimum} /> {alias('to')}{' '}
        <ValueOrLoader value={maximum} />
        <br />
        {alias('Current value: {value}', { value: currentValue ?? '-' })}
      </FormHelperText>
    </FormControl>
  );
};

CommandsNumberInput.displayName = 'CommandsNumberInput';

export default memo(CommandsNumberInput);
